import type { FC } from 'react';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { useTranslation } from '@/hooks/useTranslation';
import type { SignalSource } from '../type/SignalsTypes';

interface SignalSourceTagsProps {
  source: SignalSource;
  /** Name of another machine this source is already linked to. */
  linkedTo?: string;
  /** Show the time of the newest point on an active source ("active · 08:44"). */
  showTime?: boolean;
}

/** HH:MM of an ISO instant, in the browser's time zone. */
function clockTime(iso: string): string {
  const date = new Date(iso);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Whether a signal source is in the database, and whether it wrote a point in the last hour. */
export const SignalSourceTags: FC<SignalSourceTagsProps> = ({
  source,
  linkedTo,
  showTime = false,
}) => {
  const { t } = useTranslation();
  const tags: Array<{ key: string; label: string; tone: BadgeTone }> = [];

  if (!source.influx) {
    tags.push({ key: 'not-found', label: t('signals.source.tags.notFound'), tone: 'warning' });
  } else if (source.liveData === true) {
    const label =
      showTime && source.lastTime
        ? t('signals.source.tags.activeAt', { time: clockTime(source.lastTime) })
        : t('signals.source.tags.active');
    tags.push({ key: 'active', label, tone: 'success' });
  } else if (source.liveData === false) {
    tags.push({ key: 'history', label: t('signals.source.tags.historyOnly'), tone: 'neutral' });
  }
  if (linkedTo) {
    tags.push({
      key: 'linked',
      label: t('signals.source.tags.linkedTo', { machine: linkedTo }),
      tone: 'primary',
    });
  }

  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="signal-source-tags">
      {tags.map((tag) => (
        <Badge key={tag.key} tone={tag.tone} size="sm" caps>
          {tag.label}
        </Badge>
      ))}
    </span>
  );
};

interface SignalSourceLineProps {
  source: SignalSource;
  /** False when the worker could not read the database: the tags would be wrong. */
  showTags?: boolean;
  /** False under a heading that already says "Signal source". */
  showLabel?: boolean;
}

/** The machine's signal source, read-only: where its signals come from. */
export const SignalSourceLine: FC<SignalSourceLineProps> = ({
  source,
  showTags = true,
  showLabel = true,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-9 flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5"
      data-testid="signal-source-line"
    >
      {showLabel && (
        <span className="text-xs text-muted-foreground">{t('signals.source.label')}</span>
      )}
      <span className="font-mono text-[13px] text-foreground">{source.name}</span>
      {showTags && <SignalSourceTags source={source} />}
    </div>
  );
};

interface MissingSignalSourceProps {
  /** What cannot happen without a source, e.g. "no prediction can run". */
  message: string;
}

/** Shown once a machine without a signal source is selected. */
export const MissingSignalSource: FC<MissingSignalSourceProps> = ({ message }) => (
  <Callout size="inline" tone="warning" data-testid="missing-signal-source">
    {message}
  </Callout>
);

interface MachineSourceHintProps {
  /** The machine's signal source name, null when it has none. */
  source: string | null;
}

/** Next to a machine in a picker: its source name, or a "no source" tag. */
export const MachineSourceHint: FC<MachineSourceHintProps> = ({ source }) => {
  const { t } = useTranslation();
  if (source) {
    return <span className="font-mono text-2xs text-muted-foreground">{source}</span>;
  }
  return (
    <Badge tone="warning" size="sm" caps>
      {t('signals.source.tags.noSource')}
    </Badge>
  );
};
